import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { shouldShowDecimals } from "./showDecimals";

import { CCTable } from "components/ui/Table";
import { ButtonAsLink } from "components/ui/ButtonAsLink";
import { DisclosureChevron } from "./DisclosureChevron";
import { formatCurrency } from "../../../functions/formatCurrency";

export function FundingOffered({ currentYearAid }) {
  const {
    totalFundingOffered,
    giftAidAndOutsideResources,
    grantsAndScholarships,
    outsideResources,
    waiversAndOtherFunding,
    feeWaivers,
    thirdPartyContracts,
    loansAndWorkStudy,
    workStudy,
    loans,
  } = currentYearAid;

  const showDecimals = shouldShowDecimals(currentYearAid);

  return (
    <div style={{ paddingLeft: `var(--medium-spacing)` }}>
      <FundingTableStyles>
        <thead>
          <tr>
            <th>Funding Offered</th>
            <td className="currency">
              {formatCurrency(totalFundingOffered, { showDecimals })}
            </td>
          </tr>
        </thead>

        <FundingTableBody
          title="Gift Aid and Outside Resources"
          total={giftAidAndOutsideResources}
          showDecimals={showDecimals}
          rows={[
            { label: "Grants and Scholarships", value: grantsAndScholarships },
            { label: "Outside Resources", value: outsideResources },
          ]}
        />

        <FundingTableBody
          title="Waivers and Other Funding"
          total={waiversAndOtherFunding}
          showDecimals={showDecimals}
          rows={[
            { label: "Fee Waivers", value: feeWaivers },
            { label: "Third Party Contracts", value: thirdPartyContracts },
          ]}
        />

        <FundingTableBody
          title="Loans and Work-Study"
          total={loansAndWorkStudy}
          showDecimals={showDecimals}
          rows={[
            { label: "Work-Study", value: workStudy },
            { label: "Loans", value: loans },
          ]}
        />
      </FundingTableStyles>
    </div>
  );
}

FundingOffered.propTypes = {
  currentYearAid: PropTypes.shape({
    totalFundingOffered: PropTypes.number,
    giftAidAndOutsideResources: PropTypes.number,
    grantsAndScholarships: PropTypes.number,
    outsideResources: PropTypes.number,
    waiversAndOtherFunding: PropTypes.number,
    feeWaivers: PropTypes.number,
    thirdPartyContracts: PropTypes.number,
    loansAndWorkStudy: PropTypes.number,
    workStudy: PropTypes.number,
    loans: PropTypes.number,
  }),
};

const FundingTableBody = ({ title, total, showDecimals, rows = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const displayedRows = rows.filter((row) => row.value !== 0);

  return (
    <tbody>
      <tr>
        <th>{title}</th>
        <td className="currency">{formatCurrency(total, { showDecimals })}</td>
        <td style={{ paddingLeft: `var(--small-spacing)` }}>
          {total > 0 && (
            <ButtonAsLink onClick={() => setExpanded(!expanded)}>
              <DisclosureChevron expanded={expanded} about={title} />
            </ButtonAsLink>
          )}
        </td>
      </tr>

      {expanded &&
        displayedRows.map(({ label, value }) => (
          <tr key={label}>
            <td className="label">{label}</td>
            <td className="currency">
              {formatCurrency(value, { showDecimals })}
            </td>
            <td></td>
          </tr>
        ))}
    </tbody>
  );
};

FundingTableBody.propTypes = {
  title: PropTypes.string,
  total: PropTypes.number,
  showDecimals: PropTypes.bool,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    })
  ),
};

const FundingTableStyles = styled(CCTable)`
  // // Use to visually align the content with the inset NetCost table
  // margin-left: var(--medium-spacing);
  // margin-right: var(--medium-spacing);

  thead tr {
    th,
    td {
      font-weight: bold;
      font-size: 14px;
    }
  }

  tr th {
    font-weight: normal;
  }

  tr th,
  tr td {
    color: var(--tundora) !important;
  }

  tr td.label {
    padding-left: var(--lg);
  }

  tr td.currency {
    text-align: right;
    white-space: nowrap;
  }

  tbody:hover tr {
    th,
    td {
      background-color: var(--foam);
    }
  }
`;
